import { alongTrackM, NM_TO_M, type AirportData } from '@atc/core';
import type { Aircraft } from './aircraft';
import type { Instruction } from './commands';
import { occupiedEnd, runwayUnable, runwaysIntersect } from './runways';

/** Authored game minima, not a claim to reproduce facility directives or an operational LOA. */
export const TOWER_PROFILE = {
  id: 'ksfo-tower-v1',
  description: 'Simplified tower procedures; timing margins and missed approach are authored for this game.',
  departureGapS: 100,
  lineUpGapS: 55,
  runwayReleaseMarginS: 20,
  heavyWakeS: 120,
  heavyToLightWakeS: 180,
};

export function thresholdTimeS(ac: Aircraft): number {
  const rw = ac.approach?.runway;
  if (!rw || !ac.approach?.established || ac.onGround) return Infinity;
  return Math.max(0, -alongTrackM(rw.lat, rw.lon, rw.headingDeg, ac.lat, ac.lon) / NM_TO_M) / Math.max(60, ac.groundspeedKt) * 3600;
}

export function towerRunwayRisk(airport: AirportData, active: readonly Aircraft[], ac: Aircraft, instruction: Instruction): string | null {
  if (!['land', 'takeoff', 'lineUp'].includes(instruction.kind)) return null;
  const rw = instruction.kind === 'land' ? airport.runways.flatMap((r) => r.ends).find((e) => e.name === instruction.runway) : ac.departureRunway;
  if (!rw) return null;
  if (instruction.kind !== 'land') {
    const queueRisk = runwayUnable(airport, active, ac, instruction);
    if (queueRisk) return queueRisk;
  }
  for (const other of active) {
    if (other === ac) continue;
    const occupied = occupiedEnd(other, airport);
    if (occupied && runwaysIntersect(airport, rw, occupied)) {
      // A landing clearance can anticipate a moving operation clearing in time. A stationary
      // line-up, held crossing, or blocked rollout has no promised release time.
      const moving = ['takeoffRoll', 'departure', 'vacating'].includes(other.phase) && other.groundspeedKt > 5;
      const estimatedRelease = other.phase === 'vacating' ? 30 : 70;
      if (instruction.kind !== 'land' || !moving || thresholdTimeS(ac) < estimatedRelease + TOWER_PROFILE.runwayReleaseMarginS) return `runway ${rw.name} occupied by ${other.callsign}`;
    }
    if (instruction.kind !== 'land' && other.approach?.established && runwaysIntersect(airport, rw, other.approach.runway)) {
      const required = instruction.kind === 'takeoff' ? TOWER_PROFILE.departureGapS : TOWER_PROFILE.lineUpGapS;
      if (thresholdTimeS(other) < required) return `${other.callsign} reaches the threshold in ${Math.round(thresholdTimeS(other))} seconds; allow ${required} seconds`;
    }
    if (instruction.kind === 'takeoff' && other.departureRunway && other.takeoffAtAgeS !== undefined && other.category === 'heavy' && runwaysIntersect(airport, rw, other.departureRunway)) {
      const required = ac.category === 'light' ? TOWER_PROFILE.heavyToLightWakeS : TOWER_PROFILE.heavyWakeS;
      const left = Math.ceil(required - (other.ageS - other.takeoffAtAgeS));
      if (left > 0) return `wake separation behind ${other.callsign}: wait ${left} seconds`;
    }
  }
  return null;
}
